import SaveAltIcon from '@mui/icons-material/SaveAlt';
import Storefront from '@mui/icons-material/Storefront';
import { Box, Button, Menu, MenuItem, TextField, Typography, useTheme } from '@mui/material';
import { green } from '@mui/material/colors';
import { format } from 'date-fns';
import React, { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { DateFormatsEnum } from '../../../constants/date';
import { DEFAULT_PAGE_NUMBER, RowsPerPageEnum } from '../../../constants/oneLink';

import { routes, URL_VARS } from '../../../constants/routing';
import { useGetEngagements } from '../../../service/EngagementService';
import { toggleDownloadSuccessModal } from '../../../store/actions/modals';
import { downloadReportsCSVRequest } from '../../../store/actions/reports';
import { selectAdminType } from '../../../store/selectors/auth';
import { selectIsProductMarketplaceEnabled } from '../../../store/selectors/organizations';
import { IOneLinkWithCampaignSummaries } from '../../../types/oneLink';
import { IDownloadReportsCSVRequestPayload, IReport, ReportTypesEnum } from '../../../types/reports';
import { IApiError, IDownloadCSVSuccessPayload, NotificationListEnum } from '../../../types/shell';
import notification from '../../../utils/notification';
import {
  formatMarketplaceSpent,
  getReportGiftBudget,
  getReportItemSources,
  getReportStatusAndDate,
  hasMarketplaceItemSource,
  hasMixedItemSources,
} from '../../../utils/reports';
import { ReportsStatus } from '../../index';
import ReportItemSource from '../../ReportsComponent/components/ReportItemSource';
import { OrderLinkButton, TableMUI } from '../../_mui';
import { ITableColumn } from '../../_mui/_tables_/Table';
import { useOneLinkSourceCounts } from '../hooks/useOneLinkSourceCounts';
import { Section } from './common';

import variables from '../../../_variables.scss';

const EMPTY_SPENT_LABEL = '—';
const NOT_USED_SPENT_LABEL = 'Not used';
const MARKETPLACE_PICKS_LABEL = 'Marketplace picks';

interface IProps {
  instance?: IOneLinkWithCampaignSummaries | null;
}

export const ConfirmedSendsSection: React.FC<IProps> = ({ instance }) => {
  const theme = useTheme();
  const adminType = useSelector(selectAdminType);
  const isMarketplaceEnabled = useSelector(selectIsProductMarketplaceEnabled);
  const [searchValue, setSearchValue] = useState('');
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(DEFAULT_PAGE_NUMBER);
  const [pageSize, setPageSize] = useState(RowsPerPageEnum.Ten);
  const { enabled_2fa, recipients_count, max_submit_count, use_times_count } = instance ?? {};

  const { data, isFetching } = useGetEngagements({
    page,
    page_size: pageSize,
    ...(query && { search_query: query }),
    ...(instance?.uid && { one_link_id: instance.uid }),
  });
  const dispatch = useDispatch();

  const areMarketplaceColumnsShown = isMarketplaceEnabled && hasMarketplaceItemSource(instance);
  const isMarketplacePicksShown = isMarketplaceEnabled && hasMixedItemSources(instance);

  const sourceCounts = useOneLinkSourceCounts(instance?.uid, {
    enabled: isMarketplacePicksShown && !!instance?.uid,
  });

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const handleClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setQuery(searchValue);
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [searchValue]);

  const tableConfig: ITableColumn<IReport>[] = [
    {
      label: 'Recipient',
      render: ({ receiver_first_name, receiver_last_name }: IReport) => (
        <Typography variant="body2">
          {receiver_first_name} {receiver_last_name}
        </Typography>
      ),
    },
    {
      label: 'Status',
      render: (engagement: IReport) => {
        const { label: statusLabel, color: statusColor } = getReportStatusAndDate(engagement);
        return <ReportsStatus status={statusLabel} backgroundColor={statusColor} tiny />;
      },
    },
    {
      label: 'Order ID',
      render: ({ uid, order_id }: IReport) => (
        <OrderLinkButton
          orderId={order_id}
          reportUrl={routes.reports.getReportsUrl({
            reportId: uid,
            type: ReportTypesEnum.Regular,
            flowId: URL_VARS.SUMMARY,
          })}
        />
      ),
    },
    ...(areMarketplaceColumnsShown
      ? [
          {
            label: 'Item source',
            render: (engagement: IReport) => <ReportItemSource sources={getReportItemSources(engagement)} />,
          },
          {
            label: 'Spent',
            render: (engagement: IReport) => {
              const budget = getReportGiftBudget(engagement);

              if (!budget) {
                return <Typography variant="body2">{EMPTY_SPENT_LABEL}</Typography>;
              }

              const spent = budget.used ?? 0;

              if (!spent) {
                return (
                  <Typography variant="body2" color="text.disabled">
                    {NOT_USED_SPENT_LABEL}
                  </Typography>
                );
              }

              return <Typography variant="body2">{formatMarketplaceSpent(spent, budget.currency)}</Typography>;
            },
          },
        ]
      : []),
    {
      label: 'Submitted date',
      render: (engagement: IReport) => {
        const { createdAtDate } = getReportStatusAndDate(engagement);
        return <Typography variant="body2">{format(new Date(createdAtDate), DateFormatsEnum.ddMMMyyyy)}</Typography>;
      },
    },
  ];

  const handleSetPerPage = useCallback((rowCount: number) => {
    setPage(DEFAULT_PAGE_NUMBER);
    setPageSize(rowCount);
  }, []);

  const handleSearch = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
    setPage(DEFAULT_PAGE_NUMBER);
  }, []);

  const pageOptions = [RowsPerPageEnum.Ten, RowsPerPageEnum.Twenty, RowsPerPageEnum.Fifty];

  const downloadReportsCSV = React.useCallback(
    (payload?: Partial<IDownloadReportsCSVRequestPayload>) => {
      dispatch(
        downloadReportsCSVRequest({
          type: ReportTypesEnum.Regular,
          one_link_id: instance?.uid,
          ...payload,
        }),
      );
    },
    [adminType, dispatch, instance?.uid],
  );

  const handleDownloadClick = React.useCallback(
    (inventoryBreakdown: boolean) => {
      const downloadReportsCSVPromise = new Promise<IDownloadCSVSuccessPayload>((resolve, reject) => {
        downloadReportsCSV({ resolve, reject, inventory_breakdown: inventoryBreakdown, one_link_id: instance?.uid });
      }).then((response: IDownloadCSVSuccessPayload) => {
        dispatch(toggleDownloadSuccessModal(response));
      });

      notification.promise<void | IApiError>(NotificationListEnum.DownloadData, {
        promise: downloadReportsCSVPromise,
        promiseParams: {
          pending: 'Downloading process started. We’ll notify you when the file is ready!',
          error: {
            render: ({ data: error }) =>
              (error && error.message) || "Something bad has happened. CSV file wasn't created",
          },
        },
      });
    },
    [downloadReportsCSV, dispatch, instance?.uid],
  );

  const handleCampaignDownload = useCallback(
    (inventoryBreakdown: boolean) => {
      handleDownloadClick(inventoryBreakdown);
      handleClose();
    },
    [handleDownloadClick, handleClose],
  );

  const { sendsLeftToConfirm, showSendsLeft } = useMemo(() => {
    const recipientsCount = recipients_count ?? 0;
    const useTimesCount = use_times_count ?? 0;

    const redemptionCap = max_submit_count || undefined;
    const twoFACap = enabled_2fa && recipientsCount > 0 ? recipientsCount : undefined;

    const caps = [redemptionCap, twoFACap].filter((cap): cap is number => cap !== undefined);

    if (caps.length === 0) {
      return { sendsLeftToConfirm: 0, showSendsLeft: false };
    }

    return { sendsLeftToConfirm: Math.max(0, Math.min(...caps) - useTimesCount), showSendsLeft: true };
  }, [enabled_2fa, recipients_count, max_submit_count, use_times_count]);

  const boxStyle = useMemo(
    () => ({ fontWeight: theme.typography.fontWeightRegular, display: 'flex' }),
    [theme.typography.fontWeightRegular],
  );

  const RenderStatus = useCallback(
    ({ backgroundColor, label, value }: { backgroundColor: string; label: string; value?: number }) => (
      <Box style={boxStyle}>
        <ReportsStatus backgroundColor={backgroundColor} />
        {label}: <Typography style={{ marginLeft: 5 }}>{value}</Typography>
      </Box>
    ),
    [boxStyle],
  );

  const renderTitle = useCallback(() => {
    return (
      <Box sx={{ display: 'flex', gap: 2 }}>
        Sends:
        <RenderStatus backgroundColor={green[500]} label="Confirmed" value={use_times_count} />
        {showSendsLeft && (
          <RenderStatus backgroundColor={variables.orangeSun} label="Left" value={sendsLeftToConfirm} />
        )}
        {isMarketplacePicksShown && (
          <Box style={boxStyle} data-testid="marketplace-picks">
            <Storefront sx={{ fontSize: 18, color: variables.marketplaceAccent, mr: 0.5 }} />
            {MARKETPLACE_PICKS_LABEL}: <Typography style={{ marginLeft: 5 }}>{sourceCounts.marketplace}</Typography>
          </Box>
        )}
      </Box>
    );
  }, [use_times_count, sendsLeftToConfirm, showSendsLeft, isMarketplacePicksShown, sourceCounts.marketplace, boxStyle]);

  const renderCSVMenuButton = useCallback(() => {
    return (
      <>
        <Button onClick={handleClick}>
          <Box sx={{ display: 'flex', gap: 1, ml: 'auto' }}>
            <SaveAltIcon />
            <Typography variant="body2">CSV</Typography>
          </Box>
        </Button>
        <Menu anchorEl={anchorEl} open={!!anchorEl} onClose={handleClose}>
          <MenuItem onClick={() => handleCampaignDownload(false)}>Campaign Report</MenuItem>
          <MenuItem onClick={() => handleCampaignDownload(true)}>Product Report</MenuItem>
        </Menu>
      </>
    );
  }, [handleCampaignDownload, handleClose, handleClick, anchorEl]);

  return (
    <Section
      title={
        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <>
            {renderTitle()}
            {renderCSVMenuButton()}
          </>
        </Box>
      }
      minHeight={600}
    >
      <TextField
        label="Search"
        variant="outlined"
        fullWidth
        value={searchValue}
        onChange={handleSearch}
        sx={{ mb: 2 }}
        disabled={isFetching}
        inputRef={(ref) => {
          if (ref && searchValue) {
            ref.focus();
          }
        }}
      />
      <TableMUI
        containerStyle={{ maxHeight: 440, overflow: 'auto', width: '100%' }}
        totalCount={data?.total_count || 0}
        data={data?.engagements}
        columns={tableConfig}
        page={page}
        rowsPerPage={pageSize}
        onPageChange={setPage}
        onRowsPerPageChange={handleSetPerPage}
        isLoading={isFetching}
        perPageOptions={pageOptions}
        emptyPlaceholderTitle="No confirmed sends yet"
        emptyPlaceholderSubtitle="Waiting for information"
      />
    </Section>
  );
};
